import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { UserContext, ThemeContext } from '../Context';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Grid, 
  Chip,
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';

const AccountInfos = () => {
  const { user } = useContext(UserContext);
  const { theme } = useContext(ThemeContext);
  const [userInfo, setUserInfo] = useState(null);
  const [cguStatus, setCguStatus] = useState(null);
  const [error, setError] = useState('');

  // Styles adaptatifs pour le thème
  const isDark = theme === 'dark';

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const token = sessionStorage.getItem('authToken');
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        };

        const [userResponse, cguResponse] = await Promise.all([
          axios.get('/api/user', config),
          axios.get('/api/user/cgu/status', config)
        ]);

        const currentUser = userResponse.data.find(u => u.matricule === user);
        
        if (!currentUser) {
          throw new Error('Utilisateur non trouvé');
        }
        
        setUserInfo(currentUser);
        setCguStatus(cguResponse.data || null);
      } catch (err) {
        setError('Erreur lors de la récupération des informations du compte: ' + 
          (err.response?.data?.message || err.message));
        console.error(err);
      }
    };

    if (user) {
      fetchUserInfo();
    }
  }, [user]);

  if (!user) {
    return (
      <Box
        component={Paper}
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: isDark ? '#233044' : '#ffffff',
          color: isDark ? '#fff' : 'inherit',
        }}
      >
        <Alert severity="warning">Veuillez vous connecter pour voir vos informations.</Alert>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        component={Paper}
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: isDark ? '#233044' : '#ffffff',
          color: isDark ? '#fff' : 'inherit',
        }}
      >
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!userInfo) {
    return (
      <Box
        component={Paper}
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: isDark ? '#233044' : '#ffffff',
          color: isDark ? '#fff' : 'inherit',
        }}
      >
        <Box display="flex" justifyContent="center" m={2}>
          <CircularProgress sx={{ color: isDark ? '#90caf9' : 'inherit' }} />
        </Box>
      </Box>
    );
  }

  const acceptedAt = cguStatus?.currentAcceptance?.acceptedAt
    ? new Date(cguStatus.currentAcceptance.acceptedAt)
    : null;
  const formatAcceptanceDate = (date, language) => {
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    if (language === 'nl') {
      const monthsNl = [
        'januari', 'februari', 'maart', 'april', 'mei', 'juni',
        'juli', 'augustus', 'september', 'oktober', 'november', 'december'
      ];
      return `${day} ${monthsNl[date.getMonth()]} ${year} om ${hours}:${minutes} uur`;
    }

    const monthsFr = [
      'janvier', 'fevrier', 'mars', 'avril', 'mai', 'juin',
      'juillet', 'aout', 'septembre', 'octobre', 'novembre', 'decembre'
    ];
    return `${day} ${monthsFr[date.getMonth()]} ${year} a ${hours}:${minutes} heures`;
  };

  const acceptedAtText = acceptedAt ? formatAcceptanceDate(acceptedAt, userInfo.language) : null;

  return (
    <Box
      component={Paper}
      elevation={2}
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: isDark ? '#233044' : '#ffffff',
        color: isDark ? '#fff' : 'inherit',
        boxShadow: isDark 
          ? '0 4px 20px rgba(0, 0, 0, 0.5)'
          : '0 4px 20px rgba(0,0,0,0.05)',
      }}
    >
      <CardContent>
        <Typography 
          variant="h5" 
          component="h2" 
          gutterBottom
          sx={{ color: isDark ? '#90caf9' : 'inherit' }}
        >
          Informations du compte
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert severity={acceptedAtText ? 'success' : 'warning'}>
              {acceptedAtText
                ? (userInfo.language === 'nl'
                  ? `U hebt de gebruiksvoorwaarden van BlueNote aanvaard op ${acceptedAtText}.`
                  : `Vous avez accepté les Conditions Générales d’Utilisation de BlueNote le ${acceptedAtText}.`)
                : (userInfo.language === 'nl'
                  ? 'U hebt de huidige gebruiksvoorwaarden van BlueNote nog niet aanvaard.'
                  : 'Vous n’avez pas encore accepté les Conditions Générales d’Utilisation actuelles de BlueNote.')}
            </Alert>
          </Grid>

          <Grid item xs={12}>
            <Typography 
              variant="subtitle1" 
              color="text.secondary"
              sx={{ color: isDark ? '#b0bec5' : 'text.secondary' }}
            >
              Matricule
            </Typography>
            <Typography 
              variant="body1"
              sx={{ color: isDark ? '#fff' : 'inherit' }}
            >
              {userInfo.matricule}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography 
              variant="subtitle1" 
              color="text.secondary"
              sx={{ color: isDark ? '#b0bec5' : 'text.secondary' }}
            >
              Nom
            </Typography>
            <Typography 
              variant="body1"
              sx={{ color: isDark ? '#fff' : 'inherit' }}
            >
              {userInfo.lastname}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography 
              variant="subtitle1" 
              color="text.secondary"
              sx={{ color: isDark ? '#b0bec5' : 'text.secondary' }}
            >
              Prénom
            </Typography>
            <Typography 
              variant="body1"
              sx={{ color: isDark ? '#fff' : 'inherit' }}
            >
              {userInfo.firstname}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography 
              variant="subtitle1" 
              color="text.secondary"
              sx={{ color: isDark ? '#b0bec5' : 'text.secondary' }}
            >
              Sexe
            </Typography>
            <Typography 
              variant="body1"
              sx={{ color: isDark ? '#fff' : 'inherit' }}
            >
              {userInfo.sex === 'm' ? 'Masculin' : 'Féminin'}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography 
              variant="subtitle1" 
              color="text.secondary"
              sx={{ color: isDark ? '#b0bec5' : 'text.secondary' }}
            >
              Grade
            </Typography>
            <Typography 
              variant="body1"
              sx={{ color: isDark ? '#fff' : 'inherit' }}
            >
              {userInfo.first ? 'Premier ' : null}{userInfo.rank}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography 
              variant="subtitle1" 
              color="text.secondary"
              sx={{ color: isDark ? '#b0bec5' : 'text.secondary' }}
            >
              Service
            </Typography>
            <Typography 
              variant="body1"
              sx={{ color: isDark ? '#fff' : 'inherit' }}
            >
              {userInfo.service}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" gap={1}>
              {userInfo.opj ? (
              <Chip 
                label="Officier de Police Judiciaire"
                color="primary"
                variant="outlined"
                sx={{
                  borderColor: isDark ? '#90caf9' : 'inherit',
                  color: isDark ? '#90caf9' : 'inherit',
                  '& .MuiChip-label': {
                    color: isDark ? '#90caf9' : 'inherit'
                  }
                }}
              />) : null}
              {userInfo.opa ? (
              <Chip 
                label="Officier de Police Administrative"
                color="primary"
                variant="outlined"
                sx={{
                  borderColor: isDark ? '#90caf9' : 'inherit',
                  color: isDark ? '#90caf9' : 'inherit',
                  '& .MuiChip-label': {
                    color: isDark ? '#90caf9' : 'inherit'
                  }
                }}
              />) : null}
              {userInfo.moderator ? (
              <Chip 
                label="Modérateur"
                color="secondary"
                variant="outlined"
                sx={{
                  borderColor: isDark ? '#64b5f6' : 'inherit',
                  color: isDark ? '#64b5f6' : 'inherit',
                  '& .MuiChip-label': {
                    color: isDark ? '#64b5f6' : 'inherit'
                  }
                }}
              />) : null}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Box>
  );
};

export default AccountInfos;